/**
 * PageRouteRegistry.js
 * Centralized registry of all application routes and natural-language aliases for Kisan Mithr.
 * 
 * Supports:
 * - Direct route paths
 * - Multilingual aliases (English, Telugu, Hindi)
 * - Role requirements (farmer, buyer, admin, guest)
 * - Page descriptions for contextual inquiries ("What page am I on?", "What can I do here?")
 */

export const PAGE_ROUTES = [
  {
    id: "LANDING",
    name: "Home",
    path: "/",
    allowedRoles: ["guest", "farmer", "buyer", "admin"],
    description: "Kisan Mithr landing page and public introduction.",
    aliases: [
      "landing", "landing page", "welcome", "welcome page", "main page",
      "హోమ్", "ల్యాండింగ్",
      "होम", "लैंडिंग"
    ]
  },
  {
    id: "HOME",
    name: "Home",
    path: "/dashboard",
    fallbackPath: "/",
    allowedRoles: ["farmer", "buyer", "admin"],
    description: "Your primary farmer dashboard with quick access to all agricultural tools.",
    aliases: [
      "dashboard", "home", "go home", "take me home", "my dashboard", "farmer dashboard", "main dashboard",
      "డ్యాష్‌బోర్డ్", "హోమ్ పేజీ", "ఇంటికి వెళ్లు",
      "डैशबोर्ड", "होम", "मेरा डैशबोर्ड", "घर जाओ"
    ]
  },
  {
    id: "WEATHER",
    name: "Weather",
    path: "/weather",
    allowedRoles: ["farmer", "buyer", "admin", "guest"],
    description: "Hyper-local agricultural weather forecast, rain probability, and climate insights.",
    aliases: [
      "weather", "weather page", "weather forecast", "today weather", "rain forecast", "climate", "temperature", "forecast", "rain",
      "వాతావరణం", "వాతావరణ సమాచారం", "వర్షం",
      "मौसम", "आज का मौसम", "मौसम की जानकारी", "बारिश"
    ]
  },
  {
    id: "MARKET",
    name: "Mandi",
    path: "/market",
    allowedRoles: ["farmer", "buyer", "admin", "guest"],
    description: "Live daily mandi and agricultural commodity market prices across Indian districts.",
    aliases: [
      "mandi", "mandi prices", "mandi rates", "market", "market prices", "crop prices", "commodity rates", "live mandi", "mandy",
      "మార్కెట్ ధరలు", "మండి రేట్లు", "పంట ధరలు", "మండి ధరలు", "ధరలు", "మండి",
      "मंडी भाव", "मंडी", "बाजार भाव", "फसल के दाम", "आज का मंडी भाव"
    ]
  },
  {
    id: "CROP_HEALTH",
    name: "Crop Health",
    path: "/crop-health",
    allowedRoles: ["farmer", "admin"],
    description: "Crop disease diagnosis and visual leaf health assessment.",
    aliases: [
      "crop health", "crop disease", "plant disease", "disease diagnosis", "leaf disease", "check crop health", "crop doctor", "crop",
      "పంట ఆరోగ్యం", "పంట వ్యాధులు", "రోగాల గుర్తింపు",
      "फसल स्वास्थ्य", "फसल रोग", "पौधों की बीमारी", "पत्ता रोग"
    ]
  },
  {
    id: "SATELLITE",
    name: "Satellite Cockpit",
    path: "/satellite-cockpit",
    allowedRoles: ["farmer", "admin"],
    description: "NASA satellite NDVI vegetation health and field moisture telemetry.",
    aliases: [
      "satellite", "satellite cockpit", "nasa satellite", "satellite telemetry", "satellite imagery", "satellite health",
      "శాటిలైట్", "శాటిలైట్ కాక్‌పిట్", "ఉపగ్రహం",
      "सैटेलाइट", "सैटेलाइट कॉकपिट", "नासा सैटेलाइट"
    ]
  },
  {
    id: "PEST_DETECT",
    name: "Pest Detector",
    path: "/pest-detect",
    allowedRoles: ["farmer", "admin"],
    description: "AI-driven insect and pest detection tool with recommended treatments.",
    aliases: [
      "pest", "pest detect", "pest detector", "detect pests", "bug detector", "insects detector", "pests and disease",
      "పురుగుల గుర్తింపు", "కీటకాలు", "తెగుళ్లు",
      "कीट पहचान", "कीड़े की पहचान", "कीट"
    ]
  },
  {
    id: "FARM_PROFILE",
    name: "My Field",
    path: "/farm-profile",
    allowedRoles: ["farmer", "admin", "guest"],
    description: "Manage your farm location, soil type, irrigation methods, and crop acreage.",
    aliases: [
      "my field", "field", "open my field", "go to my field", "show my field", "my farm", "farm", "farm profile", "farm details", "manage farm", "farm settings",
      "నా వ్యవసాయం", "వ్యవసాయం", "నా పొలం", "నా చేను", "చేను", "పొలం", "కమతం", "ఫారమ్ ప్రొఫైల్", "వ్యవసాయ వివరాలు",
      "मेरा खेत", "खेत", "फार्म प्रोफाइल", "खेत की जानकारी", "फार्म"
    ]
  },
  {
    id: "SOIL",
    name: "Soil",
    path: "/farm-profile",
    allowedRoles: ["farmer", "admin", "guest"],
    description: "Manage soil composition, pH testing, and soil health parameters.",
    aliases: [
      "soil", "soil health", "soil test", "soil type", "soil info", "soil data",
      "మట్టి", "భూమి రకం", "నేల రకం",
      "मिट्टी", "मृदा", "मिट्टी की जांच"
    ]
  },
  {
    id: "LIVESTOCK",
    name: "Livestock Guide",
    path: "/livestock",
    allowedRoles: ["farmer", "admin", "guest"],
    description: "Livestock care, cattle management, vaccination schedules, and dairy farming guide.",
    aliases: [
      "livestock", "livestock guide", "cattle guide", "dairy guide", "animal husbandry", "cows", "buffaloes",
      "పశుసంరక్షణ", "పశువులు", "పాడి పరిశ్రమ",
      "पशुपालन", "गायों की देखभाल", "डेयरी"
    ]
  },
  {
    id: "SCHEMES",
    name: "Schemes",
    path: "/schemes",
    allowedRoles: ["farmer", "admin", "guest"],
    description: "Central and state government agricultural subsidies, grants, and farmer welfare schemes.",
    aliases: [
      "schemes", "government schemes", "gov schemes", "subsidies", "subsidy", "farmer subsidies", "yojana", "open schemes",
      "ప్రభుత్వ పథకాలు", "రైతు పథకాలు", "పథకాలు", "సబ్సిడీలు", "సబ్సిడీ",
      "सरकारी योजनाएं", "किसान योजना", "योजना", "सब्सिडी"
    ]
  },
  {
    id: "PESTICIDE_CALC",
    name: "Calculator",
    path: "/pesticide-calc",
    allowedRoles: ["farmer", "admin", "guest"],
    description: "Calculate precise pesticide dosage, spray dilution ratios, and tank volume.",
    aliases: [
      "pesticide calculator", "pesticide calc", "spray calculator", "dosage calculator", "calculate pesticide", "calculator",
      "పురుగుమందుల కాలిక్యులేటర్", "మందుల మోతాదు",
      "कीटनाशक कैलकुलेटर", "स्प्रे कैलकुलेटर", "दवा की मात्रा"
    ]
  },
  {
    id: "ANALYTICS",
    name: "Analytics & Growth",
    path: "/analytics-growth",
    allowedRoles: ["farmer", "admin", "guest"],
    description: "Historical crop yield analytics, cost reports, and growth performance charts.",
    aliases: [
      "analytics", "growth", "growth analytics", "yield chart", "analytics and growth",
      "ఎనలిటిక్స్", "దిగుబడి విశ్లేషణ",
      "एनालिटिक्स", "ग्रोथ रिपोर्ट", "फसल विकास"
    ]
  },
  {
    id: "OVERVIEW",
    name: "Platform Overview",
    path: "/overview",
    allowedRoles: ["farmer", "admin", "guest"],
    description: "Comprehensive summary of farm operations, equipment, and recent alerts.",
    aliases: [
      "overview", "platform overview", "farm overview",
      "అవలోకనం", "ప్లాట్‌ఫారమ్ అవలోకనం",
      "अवलोकन", "प्लेटफॉर्म अवलोकन"
    ]
  },
  {
    id: "LOCAL_ASSISTANT",
    name: "Voice Assistant",
    path: "/assistant",
    allowedRoles: ["guest", "farmer", "buyer", "admin", "user"],
    description: "Deep AI agricultural conversational advisor for detailed farming inquiries.",
    aliases: [
      "voice assistant", "ai assistant", "open voice assistant", "open ai voice assistant", "start voice assistant", "ai farm assistant", "chatbot", "local voice assistant", "assistant", "advisor", "ask kisan mithr", "ai voice assistant",
      "వాయిస్ అసిస్టెంట్", "సహాయకుడు", "ఏఐ అసిస్టెంట్", "కిసాన్ మిత్ర ఏఐ",
      "वॉइस असिस्टेंट", "सहायक", "एआई सहायक", "किसान मित्र एआई"
    ]
  },
  {
    id: "CONTACT",
    name: "Contact",
    path: "/contact",
    allowedRoles: ["guest", "farmer", "buyer", "admin"],
    description: "Contact Kisan Mithr support via inquiry form, phone, or WhatsApp.",
    aliases: [
      "contact", "contact us", "open contact", "contact page", "support", "contact support", "help", "help desk", "customer support",
      "సంప్రదించండి", "మద్దతు", "హెల్ప్ డెస్క్", "సహాయం",
      "संपर्क करो", "संपर्क", "कस्टमर सपोर्ट", "सहायता", "मदद"
    ]
  },
  {
    id: "HELP",
    name: "Help",
    path: "/contact",
    allowedRoles: ["guest", "farmer", "buyer", "admin"],
    description: "Help desk and customer support.",
    aliases: [
      "help", "help desk", "open help", "support", "assistance",
      "సహాయం", "మద్దతు",
      "मदद", "सहायता"
    ]
  },
  {
    id: "PROFILE",
    name: "Profile",
    path: "/profile",
    allowedRoles: ["farmer", "buyer", "admin", "guest"],
    description: "Manage personal account details, language preferences, and contact info.",
    aliases: [
      "profile", "my profile", "open profile", "my account", "account",
      "నా ప్రొఫైల్", "నా ఖాతా",
      "मेरी प्रोफाइल", "प्रोफाइल", "मेरा खाता"
    ]
  },
  {
    id: "SETTINGS",
    name: "Settings",
    path: "/profile",
    allowedRoles: ["farmer", "buyer", "admin", "guest"],
    description: "Account settings, preferences, and security.",
    aliases: [
      "settings", "open settings", "user settings", "account settings",
      "సెట్టింగ్స్", "సెట్టింగ్‌లు",
      "सेटिंग्स"
    ]
  },
  {
    id: "NOTIFICATIONS",
    name: "Notifications",
    path: "/notifications",
    allowedRoles: ["farmer", "buyer", "admin", "guest"],
    description: "View important farm alerts, weather warnings, and system notifications.",
    aliases: [
      "notifications", "alerts", "my notifications", "messages", "open notifications",
      "నోటిఫికేషన్లు", "హెచ్చరికలు", "సందేశాలు",
      "नोटिफिकेशन", "अलर्ट्स", "सूचनाएं"
    ]
  },
  {
    id: "BUYER_MARKET",
    name: "Buyer Marketplace",
    path: "/buyer-dashboard/market",
    fallbackPath: "/market",
    allowedRoles: ["buyer", "admin", "guest"],
    description: "Agricultural trading marketplace to source produce directly from verified farmers.",
    aliases: [
      "marketplace", "buyer market", "buyer marketplace", "buy crops", "trade produce",
      "మార్కెట్‌ప్లేస్", "కొనుగోలు బజారు",
      "मार्केटप्लेस", "फसल खरीदो"
    ]
  },
  {
    id: "OUR_TEAM",
    name: "Our Team",
    path: "/our-team",
    allowedRoles: ["guest", "farmer", "buyer", "admin"],
    description: "Meet the engineering and agricultural research team behind Kisan Mithr.",
    aliases: [
      "our team", "team", "about team", "developers", "who made this",
      "మా బృందం", "బృందం",
      "हमारी टीम", "टीम"
    ]
  },
  {
    id: "TERMS",
    name: "Terms & Privacy",
    path: "/terms",
    allowedRoles: ["guest", "farmer", "buyer", "admin"],
    description: "Terms of service, data privacy rules, and platform disclaimers.",
    aliases: [
      "terms", "terms and conditions", "privacy policy", "privacy", "terms page",
      "నిబంధనలు", "గోప్యతా విధానం",
      "नियम और शर्तें", "प्राइवेसी पॉलिसी"
    ]
  },
  {
    id: "LOGIN",
    name: "Login",
    path: "/user-login",
    allowedRoles: ["guest", "farmer", "buyer", "admin"],
    description: "Sign in to your Kisan Mithr account.",
    aliases: [
      "login", "open login", "go to login", "login page", "sign in", "user login", "log in",
      "లాగిన్", "సైన్ ఇన్",
      "लॉगिन", "साइन इन", "लॉग इन"
    ]
  },
  {
    id: "REGISTRATION",
    name: "Registration",
    path: "/user-login",
    allowedRoles: ["guest", "farmer", "buyer", "admin"],
    description: "Create a new Kisan Mithr account.",
    aliases: [
      "registration", "open registration", "go to registration", "registration page", "register", "sign up", "signup", "create account", "new account",
      "రిజిస్ట్రేషన్", "నమోదు", "సైన్ అప్",
      "पंजीकरण", "रजिस्ट्रेशन", "साइन अप"
    ]
  }
];

/**
 * Resolves natural language spoken input to a registered page object.
 * Returns the matched page definition or null if not found.
 */
export const resolveRoute = (spokenText) => {
  if (!spokenText || typeof spokenText !== "string") return null;
  let clean = spokenText.toLowerCase().trim().replace(/^[.,!?;:'"/\\]+|[.,!?;:'"/\\]+$/g, "");

  // 0. Normalization: strip speech artifacts & normalize inverted verbs
  clean = clean.replace(/\b([a-z0-9\s_-]+?)\s+(?:open|kholo|teravandi|chupinchu|dikhao)$/i, "$1");
  clean = clean.replace(/^(?:open|go\s+to|go|show\s+me|show|take\s+me\s+to|navigate\s+to|navigate|view|visit|load)\s+/i, "");
  clean = clean.trim();

  // 1. Direct path match
  const directMatch = PAGE_ROUTES.find((p) => p.path === clean || p.id.toLowerCase() === clean);
  if (directMatch) return directMatch;

  // 2. Exact alias match
  for (const page of PAGE_ROUTES) {
    for (const alias of page.aliases) {
      if (clean === alias.toLowerCase()) {
        return page;
      }
    }
  }

  // 3. Substring / contains match (prioritizing longer specific aliases)
  let bestMatch = null;
  let longestMatchLength = 0;

  for (const page of PAGE_ROUTES) {
    for (const alias of page.aliases) {
      const lowerAlias = alias.toLowerCase();
      if (clean.includes(lowerAlias) || lowerAlias.includes(clean)) {
        if (lowerAlias.length > longestMatchLength) {
          longestMatchLength = lowerAlias.length;
          bestMatch = page;
        }
      }
    }
  }

  return bestMatch;
};

/**
 * Retrieves page details by exact pathname.
 */
export const getPageByPath = (pathname) => {
  if (!pathname) return null;
  const normalized = pathname.toLowerCase().replace(/\/$/, "") || "/";
  return PAGE_ROUTES.find((p) => p.path.toLowerCase() === normalized) || null;
};

/**
 * Checks if a user role is permitted to visit a given page.
 */
export const isRouteAllowed = (page, userRole = "farmer") => {
  if (!page) return false;
  const role = (userRole || "guest").toLowerCase();
  return page.allowedRoles.includes("guest") || page.allowedRoles.includes(role);
};

const PageRouteRegistry = {
  PAGE_ROUTES,
  resolveRoute,
  getPageByPath,
  isRouteAllowed
};

export default PageRouteRegistry;
